import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { config } from 'config';
import * as lscache from 'lscache';
import { SignInMethod } from '../models/signInMethod';
import { authStateStore } from '../state-store';
import { authenticateBySSOToken } from '../util/ssoTokenUtils';

const httpLink = createHttpLink({
  uri: new URL(`/graphql`, config.appAdministrationBaseUrl).toString(),
});

let client: ApolloClient<unknown>;

let isBrowserRefreshed = true;

const getAccessToken = async (): Promise<string | undefined> => {
  const signInMethod = lscache.get('signInMethod');
  let accessToken = lscache.get('accessToken');

  // If the `signInMethod` is SSO we will renew the access-token using the SSO token.
  // If the `signInMethod` is DIRECT, there will not be any access-token renewal, and the user will need to re-login after expiration.
  if (signInMethod === SignInMethod.SSO) {
    if (accessToken === null || isBrowserRefreshed) {
      isBrowserRefreshed = false;

      accessToken = await authenticateBySSOToken(
        lscache.get('currentTenantId'),
      );
    }
  }

  // If the access token is not available force the user to login again.
  // When the SignInMethod is SSO, this state update is done within authenticateBySSOToken/getSSOToken.
  if (signInMethod === SignInMethod.DIRECT && !accessToken) {
    authStateStore.setState({
      accessToken: '',
    });
  }

  return accessToken;
};

export const apolloClient = (): ApolloClient<unknown> => {
  if (client === undefined) {
    const authLink = setContext(async (_, { headers }) => {
      const token = await getAccessToken();

      let authorizationHeader: string;
      if (token) {
        authorizationHeader = `Bearer ${token}`;
      } else {
        authorizationHeader = '';
      }

      return {
        headers: {
          ...headers,
          Authorization: authorizationHeader,
          'X-Mosaic-Client-ID': 'MOSAIC_ADMIN_PORTAL', // TODO: To be removed after tripwire is no longer needed (search for 'ADMIN_API_TRIPWIRE')
        },
      };
    });

    client = new ApolloClient({
      cache: new InMemoryCache(),
      link: authLink.concat(httpLink),
    });
  }

  return client;
};
